import cs from 'classnames';
import React from 'react';
import { LoadingLabel } from 'shared/components/Loading';
import { SummaryHeader } from 'shared/summary/SummaryHeader';
import { SummarySection } from 'shared/summary/SummarySection';
import { parseAndFormatCurrency } from 'shared/utils/currency';
import { RentalStep, slugs, useRentalSteps } from '../../app/steps';
import { CartItemFragment, Summary_ApplicationFragment } from '../../generated.graphql';
import { useItem } from '../cart/hooks';
import { NewProductDescriptionButton } from '../cart/NewProductDescription';
import { PrelovedConditionReportButton } from '../cart/PrelovedConditionReport';
import { PurchasePricingPopoverButton } from '../cart/PurchasePricing';
import styles from './SummaryCart.module.scss';
import { BaseSummaryProps } from './types';


interface ItemGridProps {
  step: RentalStep | null;
  //items?: ReadonlyArray<CartItemFragment>;
  application: Summary_ApplicationFragment;
}

const ItemGrid = ({ step, application }: ItemGridProps) => {
  const { pricing } = application;
  return (
    <div className={cs(styles.itemGrid)}>
      <SummaryHeader className={cs(styles.name, styles.gridHeader, !!application?.validation?.cart && 'text-danger')} step={step} style={{gridColumn:'1/3'}}>Items to be Rented</SummaryHeader>
      <div className={cs(styles.condition, styles.gridHeader)}>Condition</div>
      <div className={cs(styles.price, styles.gridHeader)}>Monthly Payment</div>

      <hr className={styles.gridSeparator} />
      {application.items?.map((item) => 
        <ItemRow item={item} key={item.id} readonly={!!application.inStore}/>
      )}
      <hr className={styles.gridSeparator} />

      <SummaryRow
        header="Freight, handling &amp; admin fee"
        value={parseAndFormatCurrency(pricing.setupFee)}
      />
      <SummaryRow
        header="Protection plan"
        value={parseAndFormatCurrency(application.includesProtectionPlan ? pricing.protectionPlan : 0)}
      />
      <SummaryRow
        header="Regular monthly payment"
        value={parseAndFormatCurrency(pricing.ongoingPayment)}
        className={styles.summaryTotal}
      />
    </div>
  )
}

interface ItemRowProps {
  item: CartItemFragment;
  readonly: boolean
}

const ItemRow = ({ item, readonly }: ItemRowProps) => {
  const h = useItem({item, readonly})
  return (
    <>
      <div className={styles.quantity}>{item.qty}x</div>
      <div className={styles.name}>
        <div className={styles.itemName}>{h.formattedName}</div>
        <div className={styles.itemDescription}>{item.subtitle}</div>
        <div>
          <PurchasePricingPopoverButton rate={item.selectedRate} startValue={item.startValue} className={cs('btn btn-link btn-sm text-left', styles.itemLink)} />
          {item.newProductDescription && 
            <NewProductDescriptionButton item={item} className={cs('btn btn-link btn-sm text-left', styles.itemLink)} />}
          {item.prelovedConditionReport &&
            <PrelovedConditionReportButton item={item} className={cs('btn btn-link btn-sm text-left', styles.itemLink)} />}
        </div>
      </div>
      <div className={styles.condition}>{h.formattedCondition}</div>
      <div className={styles.price}>{h.formattedRate}</div>
    </>
  );
}

interface SummaryRowProps {
  header: string;
  value: string;
  className?: string;
}

const SummaryRow = (props: SummaryRowProps) => {
  return (
    <>
      <div className={cs(props.className, styles.summaryHeader)}>{props.header}</div>
      <div className={cs(props.className, styles.summaryValue)}>{props.value}</div>
    </>
  )
}

export const CartSummary = ({ application }: BaseSummaryProps) => {
  const steps = useRentalSteps({slug: slugs.cart});
  const isInvalid = !!application?.validation?.cart;

  if (application && (application.items?.length ?? 0) > 0) {
    return (
      <SummarySection>
        <ItemGrid step={steps.this} application={application} />
      </SummarySection>
    );
  }

  return (
    <SummarySection>
      <SummaryHeader step={steps.this} className={cs(isInvalid && 'text-danger')}>Items to be rented</SummaryHeader>
      {!!application ? <p>No items in cart.</p> : <LoadingLabel alignment="left" />}
    </SummarySection>
  );
}